import {
  Directive,
  ElementRef,
  HostListener,
  AfterViewInit,
  Input,
  forwardRef,
  Renderer2
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CurrencyMaskService } from 'src/app/core/util/currency-mask.service';

const noop = () => {};

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => NumberInputDirective),
  multi: true
};

@Directive({
  selector: '[app-number-input]',
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  standalone: false
})
export class NumberInputDirective implements AfterViewInit {
  private el!: HTMLInputElement;
  // Keeps track of the value without formatting
  private innerValue: any;
  // Optional Parameter to allow for negative number interaction
  @Input() allowNegative: boolean = false;
  @Input() precision: number = 2;
  @Input() ignoreZero: boolean = false;

  @Input() defaultValue: any = undefined;
  @Input() max: number | undefined;

  constructor(
    private elementRef: ElementRef,
    private currencyMaskService: CurrencyMaskService,
    private renderer: Renderer2
  ) {
    this.el = elementRef.nativeElement;
    this.el.setAttribute('autocomplete', 'false');
  }

  // Placeholders for the callbacks which are later providesd
  // by the Control Value Accessor
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (a: any) => void = noop;

  // set getter
  get value(): any {
    return this.innerValue;
  }

  // set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  // From ControlValueAccessor interface
  writeValue(value: any) {
    if (value !== this.innerValue) {
      this.setValue(value);
      if (value) {
        this.renderer.setAttribute(
          this.elementRef.nativeElement,
          'value',
          value
        );
      }
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  ngAfterViewInit() {
    this.el.style.textAlign = 'right';
  }

  // On Focus remove all non-digit or decimal separator values
  @HostListener('focus', ['$event'])
  onfocus(event: FocusEvent) {
    const target = event.target as HTMLInputElement | null;
    if (!target) {
      return;
    }
    this.el.value = this.currencyMaskService.parse(
      target.value,
      this.precision,
      this.allowNegative
    );
    this.el.select();
  }

  // On Blur remove all symbols except last . and set to currency format
  @HostListener('blur', ['$event'])
  onBlur(event: FocusEvent) {
    const target = event.target as HTMLInputElement | null;
    if (!target) {
      return;
    }
    this.onTouchedCallback();
    this.setValue(target.value);
    this.onChangeCallback(this.innerValue);
    if (this.innerValue) {
      this.renderer.setAttribute(
        this.elementRef.nativeElement,
        'value',
        this.innerValue
      );
    }
  }

  @HostListener('keydown.enter')
  onEnter() {
    this.onTouchedCallback();
    this.setValue(this.el.value);
    this.onChangeCallback(this.innerValue);
    if (this.innerValue) {
      this.renderer.setAttribute(
        this.elementRef.nativeElement,
        'value',
        this.innerValue
      );
    }
  }

  // On Change remove all symbols except last . and set to currency format
  @HostListener('change', ['$event'])
  onChange(event: Event) {
    const target = event.target as HTMLInputElement | null;
    if (!target) {
      return;
    }
    this.setValue(this.convertFloat(target.value));
    this.onChangeCallback(this.innerValue);
    if (this.innerValue) {
      this.renderer.setAttribute(
        this.elementRef.nativeElement,
        'value',
        this.innerValue
      );
    }
  }

  // Prevent user to enter anything but digits and decimal separator
  @HostListener('keypress', ['$event'])
  onKeyPress(event: any) {
    const key = event.which || event.keyCode || 0;
    if (key === 45 && !this.allowNegative) {
      event.preventDefault();
    } else if (key === 45 && this.allowNegative) {
      // allow negative numbers
    } else if (key !== 46 && key > 31 && (key < 48 || key > 57)) {
      event.preventDefault();
    }
  }

  // Prevent user from pasting data with alphabet
  @HostListener('paste', ['$event'])
  onPaste(event: any) {
    var clipData = event.clipboardData?.getData('text');

    if (clipData) {
      const alphaRegex = /[a-zA-Z]/;
      var isInvalid = alphaRegex.test(clipData);
      if (isInvalid) event.preventDefault();
    } else {
      event.preventDefault();
    }
  }

  private setValue(value: any) {
    if (!isNaN(Number(value))) {
      let numericValue = Number(value);

      // Enforce max value if defined
      if (this.max !== undefined && numericValue > this.max) {
        numericValue = this.max;
      }

      const validNum = numericValue.toString();
      const strVal = this.currencyMaskService.format(
        validNum,
        this.precision,
        this.allowNegative,
        this.defaultValue
      );
      this.innerValue = this.currencyMaskService.parse(
        strVal,
        this.precision,
        this.allowNegative
      );

      let numVal = 0;
      if (value) numVal = +value;

      // Also check numVal against max for display logic if needed, but innerValue is key
      // Actually strictly using the formatted value based on the clamped numericValue is safest

      if (numVal === 0 && this.ignoreZero) {
        this.el.value = '';
      } else {
        this.el.value = strVal;
      }
    }
  }

  private convertFloat(val: any) {
    var cleanedText = val.replaceAll(',', '');
    return parseFloat(cleanedText);
  }
}
